import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import TechRadar from './TechRadar';

const SkillCategory = ({ category, isActive, onActivate }) => {
  const [selectedTech, setSelectedTech] = useState(null);

  const categoryData = {
    "Frontend Mastery": {
      icon: "Monitor",
      color: "#00d4ff",
      technologies: [
        {
          id: 1,
          name: "React",
          icon: "Atom",
          proficiency: 95,
          color: "#61dafb",
          description: "Advanced React development with hooks, context, and performance optimization",
          experience: "5+ years",
          projects: 50
        },
        {
          id: 2,
          name: "Next.js",
          icon: "Zap",
          proficiency: 90,
          color: "#000000",
          description: "Full-stack React framework with SSR, SSG, and API routes",
          experience: "3+ years",
          projects: 25
        },
        {
          id: 3,
          name: "TypeScript",
          icon: "Code",
          proficiency: 88,
          color: "#3178c6",
          description: "Type-safe JavaScript development with advanced type systems",
          experience: "4+ years",
          projects: 40
        },
        {
          id: 4,
          name: "Tailwind CSS",
          icon: "Palette",
          proficiency: 92,
          color: "#06b6d4",
          description: "Utility-first CSS framework for rapid UI development",
          experience: "3+ years",
          projects: 35
        },
        {
          id: 5,
          name: "Framer Motion",
          icon: "Play",
          proficiency: 85,
          color: "#ff0055",
          description: "Advanced animations and micro-interactions for React",
          experience: "2+ years",
          projects: 20
        }
      ]
    },
    "Backend Architecture": {
      icon: "Server",
      color: "#00ff88",
      technologies: [
        {
          id: 6,
          name: "Node.js",
          icon: "Hexagon",
          proficiency: 90,
          color: "#339933",
          description: "Server-side JavaScript runtime with Express and microservices",
          experience: "4+ years",
          projects: 30
        },
        {
          id: 7,
          name: "Python",
          icon: "Code2",
          proficiency: 85,
          color: "#3776ab",
          description: "Backend development with Django, FastAPI, and data processing",
          experience: "3+ years",
          projects: 25
        },
        {
          id: 8,
          name: "PostgreSQL",
          icon: "Database",
          proficiency: 88,
          color: "#336791",
          description: "Advanced database design, optimization, and complex queries",
          experience: "4+ years",
          projects: 35
        },
        {
          id: 9,
          name: "AWS",
          icon: "Cloud",
          proficiency: 82,
          color: "#ff9900",
          description: "Cloud infrastructure, serverless, and DevOps practices",
          experience: "3+ years",
          projects: 20
        },
        {
          id: 10,
          name: "Docker",
          icon: "Package",
          proficiency: 80,
          color: "#2496ed",
          description: "Containerization and orchestration for scalable deployments",
          experience: "2+ years",
          projects: 15
        }
      ]
    },
    "Design Systems": {
      icon: "Paintbrush",
      color: "#8844ff",
      technologies: [
        {
          id: 11,
          name: "Figma",
          icon: "Figma",
          proficiency: 85,
          color: "#f24e1e",
          description: "UI/UX design, prototyping, and design system creation",
          experience: "3+ years",
          projects: 40
        },
        {
          id: 12,
          name: "Adobe Creative Suite",
          icon: "Image",
          proficiency: 78,
          color: "#ff0000",
          description: "Photoshop, Illustrator, and After Effects for digital design",
          experience: "5+ years",
          projects: 60
        },
        {
          id: 13,
          name: "Three.js",
          icon: "Box",
          proficiency: 75,
          color: "#000000",
          description: "3D graphics and WebGL for immersive web experiences",
          experience: "2+ years",
          projects: 12
        },
        {
          id: 14,
          name: "GSAP",
          icon: "Sparkles",
          proficiency: 80,
          color: "#88ce02",
          description: "High-performance animations and timeline control",
          experience: "2+ years",
          projects: 18
        }
      ]
    },
    "Emerging Technologies": {
      icon: "Rocket",
      color: "#ff6b35",
      technologies: [
        {
          id: 15,
          name: "AI Integration",
          icon: "Brain",
          proficiency: 70,
          color: "#ff6b35",
          description: "OpenAI API, machine learning models, and AI-powered features",
          experience: "1+ years",
          projects: 8
        },
        {
          id: 16,
          name: "WebGL",
          icon: "Layers",
          proficiency: 65,
          color: "#990000",
          description: "Low-level graphics programming for web applications",
          experience: "1+ years",
          projects: 5
        },
        {
          id: 17,
          name: "Blockchain",
          icon: "Link",
          proficiency: 60,
          color: "#f7931a",
          description: "Smart contracts, Web3 integration, and DeFi applications",
          experience: "1+ years",
          projects: 4
        },
        {
          id: 18,
          name: "WebAssembly",
          icon: "Cpu",
          proficiency: 55,
          color: "#654ff0",
          description: "High-performance computing in the browser",
          experience: "6 months",
          projects: 2
        }
      ]
    }
  };

  const data = categoryData[category];
  if (!data) return null;

  return (
    <motion.div
      className={`relative p-8 rounded-2xl transition-all duration-500 cursor-pointer ${
        isActive 
          ? 'glass-morphism cyber-border-active cyber-glow-intense' :'bg-surface/30 cyber-border hover:bg-surface/50'
      }`}
      onClick={onActivate}
      whileHover={{ scale: isActive ? 1 : 1.02 }}
    >
      {/* Category Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <div 
            className="w-12 h-12 rounded-lg flex items-center justify-center cyber-glow"
            style={{ background: `linear-gradient(135deg, ${data.color}80, ${data.color}40)` }}
          >
            <Icon name={data.icon} size={24} color="#ffffff" />
          </div>
          <div>
            <h3 className="font-orbitron font-bold text-xl text-foreground">{category}</h3>
            <p className="text-sm text-text-secondary">{data.technologies.length} Technologies</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className={`w-3 h-3 rounded-full ${isActive ? 'bg-brand-green animate-pulse' : 'bg-text-tertiary'}`}></div>
          <span className="text-xs font-mono text-text-secondary">
            {isActive ? 'ACTIVE' : 'STANDBY'}
          </span>
        </div>
      </div>

      {/* Tech Radar */}
      <div className="mb-6">
        <TechRadar
          category={category}
          technologies={data.technologies}
          isActive={isActive}
          onTechSelect={setSelectedTech}
        />
      </div>

      {/* Quick Stats */}
      {isActive && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="grid grid-cols-3 gap-4 pt-6 border-t border-border"
        >
          <div className="text-center">
            <div className="text-2xl font-orbitron font-bold text-brand-cyan">
              {data.technologies.reduce((sum, tech) => sum + tech.projects, 0)}
            </div>
            <div className="text-xs text-text-secondary">Total Projects</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-orbitron font-bold text-brand-green">
              {Math.round(data.technologies.reduce((sum, tech) => sum + tech.proficiency, 0) / data.technologies.length)}%
            </div>
            <div className="text-xs text-text-secondary">Avg Proficiency</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-orbitron font-bold text-brand-purple">
              {Math.max(...data.technologies.map(tech => parseInt(tech.experience)))}+
            </div>
            <div className="text-xs text-text-secondary">Max Experience</div>
          </div>
        </motion.div>
      )}

      {/* Selected Technology Details */}
      {selectedTech && isActive && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 p-4 glass-morphism rounded-lg cyber-border"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              <Icon name={selectedTech.icon} size={20} color={selectedTech.color} />
              <h4 className="font-exo font-semibold text-foreground">{selectedTech.name}</h4>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedTech(null);
              }}
              className="text-text-secondary hover:text-foreground transition-colors"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
          
          <p className="text-sm text-text-secondary mb-3">{selectedTech.description}</p>
          
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div>
              <span className="text-text-tertiary">Experience:</span>
              <span className="ml-2 text-brand-cyan font-mono">{selectedTech.experience}</span>
            </div>
            <div>
              <span className="text-text-tertiary">Projects:</span>
              <span className="ml-2 text-brand-green font-mono">{selectedTech.projects}</span>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default SkillCategory;